/**
 * The typewriter and the mouth (pure): a line is revealed a character at a time at a readable pace, pausing at
 * punctuation, and the captain's mouth opens and closes once per syllable-ish chunk of what is being revealed, shut
 * during the pauses. The overlay's typewriter (typewriter.ts) and the 3D mouth (bramAnimator.ts) both read the same
 * timeline, so the words and the lips agree.
 */
import type { GuideKind } from "../guide";

export interface SpeechPace {
  /** Characters a second. */
  readonly cps: number;
  /** The whole reveal never takes longer than this (ms): a long line is sped up to fit. */
  readonly maxMs: number;
}

/**
 * Paces by step kind. Silent reading on a phone runs ~20–25 characters a second, so every pace stays ahead of the
 * reader. A `do` line is the instruction the player must act on: it is fully readable in under a second.
 */
export const SpeechPaces: Readonly<Record<GuideKind, SpeechPace>> = {
  info: { cps: 38, maxMs: 3200 },
  welcome: { cps: 40, maxMs: 2800 },
  do: { cps: 90, maxMs: 900 },
  tip: { cps: 60, maxMs: 1600 },
};

/** A beat after a comma-like mark and a longer one after a full stop (ms, before any speed-up). */
const COMMA_PAUSE_MS = 110;
const STOP_PAUSE_MS = 240;
const COMMA_MARKS = new Set([",", ";", ":", "—", "–"]);
const STOP_MARKS = new Set([".", "!", "?", "…"]);
/** Shorter mouth chunks than this are merged with the next: a mouth flapping faster than ~11 Hz reads as a flicker. */
export const MIN_SYLLABLE_MS = 90;

export interface MouthShape { readonly open: number; readonly wide: number }
const CLOSED: MouthShape = { open: 0, wide: 0 };
/** Open/wide per vowel: A open, E/I spread, O/U rounder and smaller. A chunk with no vowel ("hmm", "15") mumbles. */
const VOWEL_SHAPES: Readonly<Record<string, MouthShape>> = {
  a: { open: 0.9, wide: 0.2 }, e: { open: 0.45, wide: 0.7 }, i: { open: 0.3, wide: 0.8 },
  o: { open: 0.75, wide: 0 }, u: { open: 0.5, wide: 0 }, y: { open: 0.35, wide: 0.6 },
};
const MUMBLE: MouthShape = { open: 0.3, wide: 0.1 };

export interface Syllable { readonly t0: number; readonly t1: number; readonly shape: MouthShape }

export interface SpeechTimeline {
  /** The plain text (no `**` marks). */
  readonly text: string;
  /** When each character appears (ms from the start). */
  readonly revealAt: readonly number[];
  readonly endMs: number;
  readonly syllables: readonly Syllable[];
}

const isVowel = (c: string) => "aeiouy".includes(c.toLowerCase());
const isWordChar = (c: string) => /[\p{L}\p{N}']/u.test(c);

/** [start, end) character spans of the syllable-ish chunks of `text`: one per vowel group, consonants leading. */
export function syllableSpans(text: string): [number, number][] {
  const out: [number, number][] = [];
  let i = 0;
  while (i < text.length) {
    if (!isWordChar(text[i])) { i++; continue; }
    let end = i;
    while (end < text.length && isWordChar(text[end])) end++;
    // vowel groups inside the word
    const groups: number[] = [];
    for (let k = i; k < end; k++) if (isVowel(text[k]) && (k === i || !isVowel(text[k - 1]))) groups.push(k);
    if (groups.length <= 1) out.push([i, end]);
    else {
      let start = i;
      for (let g = 1; g < groups.length; g++) {
        // the consonant just before a vowel group opens it ("rai-ders"), unless it is still the previous group's vowel
        const cut = isVowel(text[groups[g] - 1]) ? groups[g] : groups[g] - 1;
        if (cut > start) { out.push([start, cut]); start = cut; }
      }
      out.push([start, end]);
    }
    i = end;
  }
  return out;
}

function shapeOf(text: string, a: number, b: number): MouthShape {
  for (let k = a; k < b; k++) { const s = VOWEL_SHAPES[text[k].toLowerCase()]; if (s) return s; }
  return MUMBLE;
}

/**
 * The reveal and mouth timeline of `text` at `pace`. `from` characters are already on screen (a line that changed
 * mid-step keeps what it shares with the old one): they appear at 0 and are not spoken again.
 */
export function speechTimeline(text: string, pace: SpeechPace, from = 0): SpeechTimeline {
  const charMs = 1000 / pace.cps;
  const n = text.length;
  const start = Math.max(0, Math.min(from, n));
  const raw: number[] = new Array<number>(n).fill(0);
  let t = 0;
  for (let i = start; i < n; i++) {
    t += charMs;
    raw[i] = t;
    if (i < n - 1) t += STOP_MARKS.has(text[i]) ? STOP_PAUSE_MS : COMMA_MARKS.has(text[i]) ? COMMA_PAUSE_MS : 0;
  }
  const natural = start < n ? raw[n - 1] : 0;
  const k = natural > pace.maxMs ? pace.maxMs / natural : 1;
  const revealAt = raw.map((v) => v * k);
  const step = charMs * k;

  const syllables: Syllable[] = [];
  let pending: { t0: number; t1: number; a: number; b: number } | null = null;
  const flush = () => { if (pending) syllables.push({ t0: pending.t0, t1: pending.t1, shape: shapeOf(text, pending.a, pending.b) }); pending = null; };
  for (const [a, b] of syllableSpans(text)) {
    if (b <= start) continue;
    const s = Math.max(a, start);
    const t0 = revealAt[s] - step, t1 = revealAt[b - 1];
    // merge a too-short chunk forward, but never across a pause (the mouth must shut at punctuation)
    if (pending && t0 - pending.t1 <= step + 1e-6 && pending.t1 - pending.t0 < MIN_SYLLABLE_MS) { pending.t1 = t1; pending.b = b; continue; }
    flush();
    pending = { t0, t1, a: s, b };
  }
  flush();
  return { text, revealAt, endMs: start < n ? revealAt[n - 1] : 0, syllables };
}

/** Characters on screen `ms` into the reveal. */
export function revealedAt(tl: SpeechTimeline, ms: number): number {
  let lo = 0, hi = tl.revealAt.length;
  // revealAt is non-decreasing: count the entries ≤ ms
  while (lo < hi) { const mid = (lo + hi) >> 1; if (tl.revealAt[mid] <= ms) lo = mid + 1; else hi = mid; }
  return lo;
}

/** The mouth `ms` into the reveal: one open-close arc per chunk, shut between chunks and after the end. */
export function mouthAt(tl: SpeechTimeline, ms: number): MouthShape {
  for (const s of tl.syllables) {
    if (ms < s.t0) break;
    if (ms <= s.t1 && s.t1 > s.t0) {
      const arc = Math.sin(Math.PI * ((ms - s.t0) / (s.t1 - s.t0)));
      return { open: s.shape.open * arc, wide: s.shape.wide * arc };
    }
  }
  return CLOSED;
}

/** How many leading characters two lines share (a line that follows the player keeps its shared start on screen). */
export function sharedPrefix(a: string, b: string): number {
  const n = Math.min(a.length, b.length);
  let i = 0;
  while (i < n && a[i] === b[i]) i++;
  return i;
}

/**
 * One line being said: the timeline plus its clock. `finish()` (the player's first tap) shows the rest at once and
 * shuts the mouth. Time is passed in, so tests drive it.
 */
export class Speech {
  private finishedAt: number | null = null;
  constructor(readonly timeline: SpeechTimeline, readonly startedAt: number) {}
  elapsed(now: number): number { return this.finishedAt !== null ? Infinity : now - this.startedAt; }
  revealed(now: number): number { return this.finishedAt !== null ? this.timeline.text.length : revealedAt(this.timeline, now - this.startedAt); }
  done(now: number): boolean { return this.finishedAt !== null || now - this.startedAt >= this.timeline.endMs; }
  mouth(now: number): MouthShape { return this.finishedAt !== null ? CLOSED : mouthAt(this.timeline, now - this.startedAt); }
  finish(now: number): void { if (this.finishedAt === null) this.finishedAt = now; }
}
